///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { isDefined } from '@siklu/sui-lib/utils';

@Injectable({ providedIn: 'root' })
export class GtthLocalStorageService {
    private static readonly PREFIX = 'jhi-';
    private static readonly TAB_ID_LIST = GtthLocalStorageService.PREFIX + 'tabsIdList';
    private static readonly CUSTOMER_FILTER_KEY = GtthLocalStorageService.PREFIX + 'customerFilter';
    private static readonly PROJECT_FILTER_KEY = GtthLocalStorageService.PREFIX + 'projectFilter';
    private static readonly OPENED_URLS = GtthLocalStorageService.PREFIX + 'openedUrls';
    private static readonly CURRENT_TREE_LIST_TYPE = GtthLocalStorageService.PREFIX + 'projects_list_type';
    private static readonly SELECTED_FOLDER_ID = GtthLocalStorageService.PREFIX + 'selected_folder_id';
    private static readonly SELECTED_CUSTOMER_ID = GtthLocalStorageService.PREFIX + 'selected_customer_id';

    private readonly currentTab: number;
    private readonly localStorage: Storage;

    constructor() {
        this.localStorage = window.localStorage;
        this.currentTab = this.generateNewTabId();
        this.addNewTabId(this.currentTab);

        window.addEventListener('beforeunload', (event) => {
            this.removeCurrentTab();
        });
    }

    getProjectsViewType(): string {
        return this.localStorage.getItem(GtthLocalStorageService.CURRENT_TREE_LIST_TYPE);
    }

    setProjectsViewType(value: string): void {
        this.localStorage.setItem(GtthLocalStorageService.CURRENT_TREE_LIST_TYPE, value);
    }

    getSelectedFolderId(): number {
        const value = this.localStorage.getItem(GtthLocalStorageService.SELECTED_FOLDER_ID);
        return value ? parseInt(value, 10) : undefined;
    }

    setSelectedFolderId(value: number): void {
        this.localStorage.setItem(GtthLocalStorageService.SELECTED_FOLDER_ID, '' + value);
    }

    getSelectedCustomerId(): number {
        const value = this.localStorage.getItem(GtthLocalStorageService.SELECTED_CUSTOMER_ID);
        return value ? parseInt(value, 10) : undefined;
    }

    setSelectedCustomerId(value: number): void {
        this.localStorage.setItem(GtthLocalStorageService.SELECTED_CUSTOMER_ID, isDefined(value) ? value.toString() : '');
    }

    clearSelectedCustomerId(): void {
        this.localStorage.removeItem(GtthLocalStorageService.SELECTED_CUSTOMER_ID);
    }

    getCustomerFilter(): string {
        return this.localStorage.getItem(GtthLocalStorageService.CUSTOMER_FILTER_KEY) || '';
    }

    setCustomerFilter(value: string): void {
        if (value === '') {
            this.localStorage.removeItem(GtthLocalStorageService.CUSTOMER_FILTER_KEY);
        } else {
            this.localStorage.setItem(GtthLocalStorageService.CUSTOMER_FILTER_KEY, value);
        }
    }

    getProjectFilter(): string {
        return this.localStorage.getItem(GtthLocalStorageService.PROJECT_FILTER_KEY) || '';
    }

    setProjectFilter(value: string): void {
        if (value === '') {
            this.localStorage.removeItem(GtthLocalStorageService.PROJECT_FILTER_KEY);
        } else {
            this.localStorage.setItem(GtthLocalStorageService.PROJECT_FILTER_KEY, value);
        }
    }

    changeOpenTabUrl(urlAfterRedirects: string): string[] {
        const storageValue = this.localStorage.getItem(GtthLocalStorageService.OPENED_URLS);
        const openedUrls = storageValue ? JSON.parse(storageValue) : {};
        openedUrls[this.currentTab] = urlAfterRedirects;

        const tabList = this.getTabsList();
        const result = [];
        const updatedOpenedUrls = {};
        for (const tabId of tabList) {
            if (openedUrls[tabId]) {
                result.push(openedUrls[tabId]);
                updatedOpenedUrls[tabId] = openedUrls[tabId];
            }
        }

        this.localStorage.setItem(GtthLocalStorageService.OPENED_URLS, JSON.stringify(updatedOpenedUrls));

        return result;
    }

    getOpenTabUrl(): string[] {
        const storageValue = this.localStorage.getItem(GtthLocalStorageService.OPENED_URLS);
        const openedUrls = storageValue ? JSON.parse(storageValue) : {};

        const tabList = this.getTabsList();
        const result = [];
        for (const tabId of tabList) {
            if (openedUrls[tabId]) {
                result.push(openedUrls[tabId]);
            }
        }

        return result;
    }

    getCurrentLocationUrl(): string {
        return this.getOpenTabUrl()[0] || '/';
    }

    private generateNewTabId(): number {
        let tabId = 1;
        const tabList = this.getTabsList();
        while (tabList.includes(tabId)) {
            tabId++;
        }
        return tabId;
    }

    private addNewTabId(newTabId: number): void {
        const tabList = this.getTabsList();
        tabList.push(newTabId);
        this.localStorage.setItem(GtthLocalStorageService.TAB_ID_LIST, tabList.join(','));
    }

    private removeCurrentTab(): void {
        const tabList = this.getTabsList();
        const tabIndex = tabList.indexOf(this.currentTab);
        if (tabList.length > 1) {
            tabList.splice(tabIndex, 1);
            this.localStorage.setItem(GtthLocalStorageService.TAB_ID_LIST, tabList.join(','));
        }

        const storageValue = this.localStorage.getItem(GtthLocalStorageService.OPENED_URLS);
        const openedUrls = storageValue ? JSON.parse(storageValue) : {};
        if (openedUrls[this.currentTab]) {
            openedUrls[this.currentTab] = undefined;
        }
        this.localStorage.setItem(GtthLocalStorageService.OPENED_URLS, JSON.stringify(openedUrls));
    }

    private getTabsList(): number[] {
        const currentTabList = this.localStorage.getItem(GtthLocalStorageService.TAB_ID_LIST);
        return currentTabList ? currentTabList.split(',').map((value) => parseInt(value, 10)) : [];
    }
}
